import { useState } from "react";
import { ModifierGroup } from "../types";
import useDebounce from "../hooks/useDebounce";
import {
  useDeleteModifierGroupMutation,
  useFetchModifierGroupsQuery,
  useTypedSelector,
} from "../store";
import { showToast, ToastType } from "../utils/toast.utils";
import Pagination from "../components/common/Pagination";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";
import { FaEdit, FaRegTrashAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import NavigationLinkBtn from "../components/common/NavigationLinkBtn";
import TextField from "../components/common/TextField";
import { tdClasses, thClasses } from "../configs";

const ModifierGroupsWrapper = () => {
  const restaurant = useTypedSelector((state) => state.session.restaurant);

  if (!restaurant)
    return (
      <div className="w-full flex justify-center mt-4 text-xl font-semibold">
        Veuillez sélectionner un restaurant
      </div>
    );

  return (
    <div className="w-full flex flex-col items-center justify-center">
      <ModifierGroups restaurantId={restaurant._id} />
    </div>
  );
};

interface ModifierGroupsProps {
  restaurantId: string;
}

const ModifierGroups: React.FC<ModifierGroupsProps> = ({ restaurantId }) => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [displayName, setDisplayName] = useState<string>("");
  const debouncedDislayName = useDebounce(displayName, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modifierToDelete, setModifierToDelete] =
    useState<ModifierGroup | null>(null);

  const [deleteModifierGroup] = useDeleteModifierGroupMutation();

  const confirmDelete = (modifierToDelete: ModifierGroup) => {
    setModifierToDelete(modifierToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteModifierGroup = async () => {
    if (!modifierToDelete) return;
    const result = await deleteModifierGroup(modifierToDelete._id);
    if (!result.error) {
      showToast("Groupe de supplément supprimé", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setModifierToDelete(null);
  };

  const { data: modifierGroups } = useFetchModifierGroupsQuery({
    restaurantId,
    searchQuery: {
      page,
      perPage,
      displayName: debouncedDislayName,
    },
  });

  return (
    <div className="w-full flex flex-col mx-auto p-4">
      <div className="flex items-center justify-between">
        <div className="w-1/4">
          <TextField
            id="lastName"
            value={displayName}
            onChange={(e) =>
              setDisplayName((e.target as HTMLInputElement).value)
            }
            placeholder="Nom groupe de suppléments"
          />
        </div>
        <NavigationLinkBtn
          to="/modifier-groups/create"
          label="Créer un groupe de suppléments"
        />
      </div>
      <table
        className="table-fixed w-full rounded-xl"
        style={{ borderRadius: "50px" }}
      >
        <colgroup>
          <col className="w-8/12" />
          <col className="w-2/12" />
          <col className="w-2/12" />
        </colgroup>
        <thead>
          <tr>
            <th key={0} className={thClasses}>
              Nom
            </th>
            <th key={1} className={thClasses}>
              Modifier
            </th>
            <th key={2} className={thClasses}>
              Supprimer
            </th>
          </tr>
        </thead>
        <tbody>
          {modifierGroups?.data?.map((row) => {
            return (
              <tr key={row._id}>
                <td className={tdClasses}> {row.displayName} </td>
                <td className={tdClasses}>
                  <Link className="w-full flex justify-center" to={row._id}>
                    <FaEdit className="w-8 text-primary" />{" "}
                  </Link>
                </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <FaRegTrashAlt
                      className="w-8 text-primary cursor-pointer"
                      onClick={() => {
                        confirmDelete(row);
                      }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        paginationInfo={{
          page,
          perPage,
          count: modifierGroups?.paginationInfo.count || 1,
        }}
        setPage={setPage}
        setPerPage={setPerPage}
      />
      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-300 px-4 py-2 rounded mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-primary text-white px-4 py-2 rounded"
                onClick={handleDeleteModifierGroup}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="bg-white text-center font-semibold">
            Êtes-vous sûr de vouloir supprimer {modifierToDelete?.displayName} ?
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ModifierGroupsWrapper;
